import { EffectsCommandMap } from 'dva';
import { ConnectState } from '@/models/connect';
import { FUtil, FServiceAPI, FI18n } from '@freelog/tools-lib';
import dayjs from 'dayjs';
import FileSaver from 'file-saver';
import { decompile, compile } from '@freelog/nmr_translator';
import type {
  InformalNodeManagerPageModelState,
  ChangeAction,
  Fetch_Rules_Action,
  SaveRulesAction,
  SaveDataRulesAction,
  FetchExhibitListAction,
  FetchThemeListAction,
  OnLoad_Rule_ImportFileInput_Action,
  OnClick_Rule_ExportBtn_Action,
  OnClick_Rule_Export_ConfirmBtn_Action,
  OnClick_Rule_Export_CancelBtn_Action,
  OnClick_Rule_DeleteBtn_Action,
  OnClick_Rule_Delete_ConfirmBtn_Action,
  OnClick_Rule_Delete_CancelBtn_Action,
  OnClick_Rule_EntryCodingBtn_Action,
  OnClick_Rule_ExitCodingBtn_Action,
  OnClick_Rule_ExitCoding_ConfirmBtn_Action,
  OnClick_Rule_ExitCoding_CancelBtn_Action,
  OnChange_Rule_CheckAllCheckbox_Action,
  OnChange_Rule_ListCheckbox_Action,
  OnChange_Rule_Codemirror_Action,
  OnClick_Rule_SaveBtn_Action,
  RuleMatchAndResultReturn,
} from './types';
import { ruleMatchAndResult } from './helpers';

interface RuleMatchAndResultParams {
  nodeID: number;
  isRematch?: boolean;
}

export const ruleEffects = {
  * fetch_Rules({}: Fetch_Rules_Action, { call, select, put }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    const params: Parameters<typeof FServiceAPI.InformalNode.testNodeRules>[0] = {
      nodeId: informalNodeManagerPage.node_ID,
    };

    const { data } = yield call(FServiceAPI.InformalNode.testNodeRules, params);

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        rule_CodeInput: data.ruleText,
        rule_CodeIsDirty: false,
        rule_RuleList: data.testRules
          .filter((tr: any) => {
            return tr.ruleInfo.operation !== 'comment';
          })
          .map((tr: any) => {
            return {
              ...tr,
              checked: false,
            };
          }),
        rule_CodeExecutionErrors: [],
        rule_CodeEfficients: [],
      },
    });
  },
  * saveRules({}: SaveRulesAction, { select, call, put }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        rule_CodeState: 'checking',
      },
    });

    const params: Parameters<typeof FServiceAPI.InformalNode.createRules>[0] = {
      nodeId: informalNodeManagerPage.node_ID,
      testRuleText: informalNodeManagerPage.rule_CodeInput,
    };
    const { data } = yield call(FServiceAPI.InformalNode.createRules, params);

    const params1: RuleMatchAndResultParams = {
      nodeID: informalNodeManagerPage.node_ID,
    };
    const result: RuleMatchAndResultReturn = yield call(ruleMatchAndResult, params1);

    const rule_CodeExecutionWarnings: InformalNodeManagerPageModelState['rule_CodeExecutionErrors'] = result.testRules
      .filter((tr: any) => {
        return tr.matchWarnings.length > 0;
      })
      .map((tr: any) => {
        return {
          ruleText: tr.ruleInfo.text,
          errors: tr.matchWarnings,
        };
      });

    const rule_CodeExecutionErrors: InformalNodeManagerPageModelState['rule_CodeExecutionErrors'] = result.testRules
      .filter((tr: any) => {
        return tr.matchErrors.length > 0;
      })
      .map((tr: any) => {
        return {
          ruleText: tr.ruleInfo.text,
          errors: tr.matchErrors,
        };
      });

    const rule_CodeEfficients: InformalNodeManagerPageModelState['rule_CodeEfficients'] = result.testRules
      .filter((tr: any) => {
        return tr.efficientInfos.length > 0;
      })
      .map((tr: any) => {
        return {
          ruleText: tr.ruleInfo.text,
          matchCount: tr.efficientInfos.reduce((accumulator: number, currentValue: any) => {
            return accumulator + currentValue.count;
          }, 0),
        };
      });

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        node_RuleInfo: result,
        rule_CodeIsDirty: false,
        rule_CodeState: rule_CodeExecutionErrors.length === 0 ? 'noError' : 'executionError',
        rule_CodeExecutionErrors: [
          ...rule_CodeExecutionErrors,
          ...rule_CodeExecutionWarnings,
        ],
        rule_CodeEfficients: rule_CodeEfficients,
        rule_RuleList: result.testRules.map((tr: any) => {
          return {
            ...tr,
            checked: false,
          };
        }),
      },
    });
  },

  * saveDataRules({ payload }: SaveDataRulesAction, { call, select, put }: EffectsCommandMap) {

    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    const text = decompile(payload.data);

    if (payload.type === 'append') {
      const params: Parameters<typeof FServiceAPI.InformalNode.putRules>[0] = {
        nodeId: informalNodeManagerPage.node_ID,
        additionalTestRule: text,
      };
      const { data } = yield call(FServiceAPI.InformalNode.putRules, params);
    } else if (payload.type === 'replace') {
      const params: Parameters<typeof FServiceAPI.InformalNode.createRules>[0] = {
        nodeId: informalNodeManagerPage.node_ID,
        testRuleText: text,
      };
      const { data } = yield call(FServiceAPI.InformalNode.createRules, params);
    }

    const params2: RuleMatchAndResultParams = {
      nodeID: informalNodeManagerPage.node_ID,
      isRematch: true,
    };

    const result: RuleMatchAndResultReturn = yield call(ruleMatchAndResult, params2);

    if (result.status === 2) {
      return;
    }

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        node_RuleInfo: result,
      },
    });

    if (informalNodeManagerPage.showPage === 'exhibit') {
      yield put<FetchExhibitListAction>({
        type: 'fetchExhibitList',
        payload: {
          isRematch: false,
        },
      });
    } else if (informalNodeManagerPage.showPage === 'theme') {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          theme_ActivatingThemeName: '',
        },
      });
      yield put<FetchThemeListAction>({
        type: 'fetchThemeList',
        payload: {
          isRematch: false,
        },
      });
    }
  },
  * onLoad_Rule_ImportFileInput({ payload }: OnLoad_Rule_ImportFileInput_Action, { put, select }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    const dateTime: string = dayjs().format(FUtil.Predefined.momentDateTimeFormat);
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        rule_CodeInput: '\n'
          + `// 以下语句于${dateTime}导入` + '\n'
          + payload.value + '\n'
          + `// 以上语句于${dateTime}导入` + '\n'
          + informalNodeManagerPage.rule_CodeInput,
        rule_PageStatus: 'coding',
        rule_CodeState: 'editing',
        rule_CodeIsDirty: true,
        rule_CodeCompileErrors: [],
        rule_CodeExecutionErrors: [],
        rule_CodeEfficients: [],
      },
    });
  },
  * onClick_Rule_ExportBtn({}: OnClick_Rule_ExportBtn_Action, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        rule_PageStatus: 'export',
      },
    });
  },
  * onClick_Rule_Export_ConfirmBtn({}: OnClick_Rule_Export_ConfirmBtn_Action, { select }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    const fileName = `测试节点${informalNodeManagerPage.node_Name} - 映射规则 - ${dayjs().format(FUtil.Predefined.momentDateFormat)}.txt`;
    const text: string = informalNodeManagerPage.rule_RuleList
      .filter((rl) => rl.checked)
      .map((rl) => {
        return rl.ruleInfo.text;
      })
      .join('\n\n');
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    FileSaver.saveAs(blob, fileName);
  },
  * onClick_Rule_Export_CancelBtn({}: OnClick_Rule_Export_CancelBtn_Action, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        rule_PageStatus: 'normal',
      },
    });
  },
  * onClick_Rule_DeleteBtn({}: OnClick_Rule_DeleteBtn_Action, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        rule_PageStatus: 'delete',
      },
    });
  },
  * onClick_Rule_Delete_ConfirmBtn({}: OnClick_Rule_Delete_ConfirmBtn_Action, { select, put }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    const text: string = informalNodeManagerPage.rule_RuleList
      .filter((rl) => !rl.checked)
      .map((rl) => {
        return rl.ruleInfo.text;
      })
      .join('\n\n');
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        rule_CodeInput: text,
      },
    });
    yield put<SaveRulesAction>({
      type: 'saveRules',
    });
  },
  * onClick_Rule_Delete_CancelBtn({}: OnClick_Rule_Delete_CancelBtn_Action, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        rule_PageStatus: 'normal',
      },
    });
  },
  * onClick_Rule_EntryCodingBtn({}: OnClick_Rule_EntryCodingBtn_Action, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        rule_PageStatus: 'coding',
      },
    });
  },
  * onClick_Rule_ExitCodingBtn({}: OnClick_Rule_ExitCodingBtn_Action, { put, select }: EffectsCommandMap) {

    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        rule_PageStatus: 'normal',

        rule_CodeInput: informalNodeManagerPage.node_RuleInfo?.ruleText || '',
        rule_CodeIsDirty: false,
        rule_CodeState: 'editing',
        rule_CodeCompileErrors: [],
        rule_CodeExecutionErrors: [],
        rule_CodeEfficients: [],
      },
    });
  },
  * onClick_Rule_ExitCoding_ConfirmBtn({}: OnClick_Rule_ExitCoding_ConfirmBtn_Action, {
    put,
    select,
  }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        rule_PageStatus: 'normal',

        rule_CodeInput: informalNodeManagerPage.node_RuleInfo?.ruleText || '',
        rule_CodeIsDirty: false,
        rule_CodeState: 'editing',
        rule_CodeCompileErrors: [],
        rule_CodeExecutionErrors: [],
        rule_CodeEfficients: [],
      },
    });
  },
  * onClick_Rule_ExitCoding_CancelBtn({}: OnClick_Rule_ExitCoding_CancelBtn_Action, {}: EffectsCommandMap) {

  },
  * OoChange_Rule_CheckAllCheckbox({ payload }: OnChange_Rule_CheckAllCheckbox_Action, {
    put,
    select,
  }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    yield put<ChangeAction>({
      type: 'change',
      payload: {
        rule_RuleList: informalNodeManagerPage.rule_RuleList.map((rpr) => {
          return {
            ...rpr,
            checked: payload.checked,
          };
        }),
      },
    });
  },
  * onChange_Rule_ListCheckbox({ payload }: OnChange_Rule_ListCheckbox_Action, { put, select }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    const rulePageRuleList: InformalNodeManagerPageModelState['rule_RuleList'] = informalNodeManagerPage.rule_RuleList.map((rl) => {
      if (rl.id !== payload.ruleID) {
        return rl;
      }
      return {
        ...rl,
        checked: payload.checked,
      };
    });
    let ruleIndeterminateChecked: boolean = false;

    if (rulePageRuleList.every((rp) => {
      return rp.checked;
    })) {
      ruleIndeterminateChecked = true;
    }
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        rule_RuleList: rulePageRuleList,
      },
    });
  },
  * onChange_Rule_Codemirror({ payload }: OnChange_Rule_Codemirror_Action, { put }: EffectsCommandMap) {
    yield put<ChangeAction>({
      type: 'change',
      payload: {
        rule_CodeInput: payload.value,
        rule_CodeIsDirty: true,
        rule_CodeState: 'editing',
        rule_CodeCompileErrors: [],
        rule_CodeExecutionErrors: [],
        rule_CodeEfficients: [],
      },
    });
  },
  * onClick_Rule_SaveBtn({}: OnClick_Rule_SaveBtn_Action, { select, put }: EffectsCommandMap) {
    const { informalNodeManagerPage }: ConnectState = yield select(({ informalNodeManagerPage }: ConnectState) => ({
      informalNodeManagerPage,
    }));

    const { errors, rules, errorObjects } = compile(informalNodeManagerPage.rule_CodeInput);
    if (errorObjects.length > 0) {
      yield put<ChangeAction>({
        type: 'change',
        payload: {
          rule_CodeCompileErrors: errorObjects,
          rule_CodeState: 'compileError',
        },
      });
      return;
    }
    yield put<SaveRulesAction>({
      type: 'saveRules',
    });
  },
};
